import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button,
  Paper,
  Divider, 
  useTheme,
  Tab,
  Tabs
} from '@mui/material';
import { FaUtensils, FaGlassMartiniAlt, FaHotdog } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RestaurantMenu from '../components/RestaurantMenu';
import BarMenu from '../components/BarMenu';
import ConcessionMenu from '../components/ConcessionMenu';

const Dining = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Navbar />
      
      {/* Hero Section */}
      <Box 
        sx={{
          height: {xs: '50vh', md: '60vh'},
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(10, 25, 41, 0.9)), url("/images/dining-hero.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 2,
                textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                animation: 'fadeInDown 1s ease-out',
                '@keyframes fadeInDown': {
                  '0%': { opacity: 0, transform: 'translateY(-20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              Dining Experiences
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.9,
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                animation: 'fadeIn 1s ease-out 0.3s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 0.9 }
                }
              }}
            >
              Elevate your cinema experience with chef-curated dining options, craft cocktails, and premium concessions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ 
        bgcolor: theme.palette.background.default,
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
      }}>
        <Container maxWidth="lg">
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="secondary"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ 
              '& .MuiTab-root': {
                py: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                color: 'text.secondary',
                '&:hover': {
                  color: 'secondary.light',
                }
              },
              '& .Mui-selected': {
                color: '#f5b400 !important',
                fontWeight: 700,
                backgroundColor: 'rgba(245, 180, 0, 0.08)',
                borderRadius: '8px 8px 0 0',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40%',
                  height: 3,
                  backgroundColor: '#f5b400',
                  borderRadius: '3px 3px 0 0',
                  boxShadow: '0 0 8px rgba(245, 180, 0, 0.5)',
                }
              },
              '& .MuiTabs-indicator': {
                height: 0, // Hide the default indicator since we're using a custom one
              }
            }}
          >
            <Tab 
              icon={<FaUtensils size={20} />} 
              iconPosition="start" 
              label="Stellar Eats Restaurant" 
            />
            <Tab 
              icon={<FaGlassMartiniAlt size={20} />} 
              iconPosition="start" 
              label="Cosmic Bar" 
            />
            <Tab 
              icon={<FaHotdog size={20} />} 
              iconPosition="start" 
              label="Premium Concessions" 
            />
          </Tabs>
        </Container>
      </Box>

      {/* Tab Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {/* Restaurant */}
          <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/restaurant2.jpg"
                  alt="Stellar Eats Restaurant"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '650px',
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
                    animation: 'fadeInLeft 0.8s ease-out',
                    '@keyframes fadeInLeft': {
                      '0%': { opacity: 0, transform: 'translateX(-50px)' },
                      '100%': { opacity: 1, transform: 'translateX(0)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    animation: 'fadeInRight 0.8s ease-out',
                    '@keyframes fadeInRight': {
                      '0%': { opacity: 0, transform: 'translateX(50px)' },
                      '100%': { opacity: 1, transform: 'translateX(0)' }
                    }
                  }}
                >
                  <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                    Stellar Eats Restaurant
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, opacity: 0.8 }}>
                    A full dining experience delivered right to your luxury seat
                  </Typography>
                  
                  <Typography paragraph sx={{ mb: 4 }}>
                    Enjoy a full menu of appetizers, entrées, and desserts while watching your favorite films. 
                    Our chef creates special themed menu items inspired by current blockbusters, making each 
                    visit a unique culinary adventure.
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      In-Theater Dining Features:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Typography component="li" paragraph>
                        Full menu of chef-crafted dishes delivered to your seat
                      </Typography>
                      <Typography component="li" paragraph>
                        Discreet service that won't disturb your movie experience
                      </Typography>
                      <Typography component="li" paragraph>
                        Special movie-themed menu items that change with new releases
                      </Typography>
                      <Typography component="li" paragraph>
                        Dietary options including vegetarian, vegan, and gluten-free
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large"
                    href="/reservation?type=restaurant"
                  >
                    Make Reservation
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 6 }}>
                <Divider sx={{ mb: 6 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
                  Sample Menu Items
                </Typography>
                <RestaurantMenu />
              </Grid>
            </Grid>
          </Box>
          
          {/* Bar */}
          <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    animation: 'fadeInLeft 0.8s ease-out',
                    '@keyframes fadeInLeft': {
                      '0%': { opacity: 0, transform: 'translateX(-50px)' },
                      '100%': { opacity: 1, transform: 'translateX(0)' }
                    }
                  }}
                >
                  <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                    Cosmic Bar
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, opacity: 0.8 }}>
                    Craft beverages to enhance your cinema experience
                  </Typography>
                  
                  <Typography paragraph sx={{ mb: 4 }}>
                    Relax before or after your show at our stylish bar featuring craft cocktails, 
                    local beers, and a curated wine selection. Our mixologists create signature 
                    drinks inspired by the movies you love.
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Bar Highlights:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Typography component="li" paragraph>
                        Signature movie-themed cocktails
                      </Typography>
                      <Typography component="li" paragraph>
                        Happy hour specials (Monday-Thursday, 4PM-6PM)
                      </Typography>
                      <Typography component="li" paragraph>
                        Rotating selection of local craft beers on tap
                      </Typography>
                      <Typography component="li" paragraph>
                        Curated wine list with perfect pairings for our food menu
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large"
                    href="/reservation?type=bar"
                  >
                    Reserve Lounge Area
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/bar1.jpg"
                  alt="Cosmic Bar"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '650px',
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
                    animation: 'fadeInRight 0.8s ease-out',
                    '@keyframes fadeInRight': {
                      '0%': { opacity: 0, transform: 'translateX(50px)' },
                      '100%': { opacity: 1, transform: 'translateX(0)' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 6 }}>
                <Divider sx={{ mb: 6 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
                  Featured Drinks
                </Typography>
                <BarMenu />
              </Grid>
            </Grid>
          </Box>
          
          {/* Concessions */}
          <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/concessions1.jpg"
                  alt="Premium Concessions"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '650px',
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
                    animation: 'fadeInLeft 0.8s ease-out',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    animation: 'fadeInRight 0.8s ease-out',
                  }}
                >
                  <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                    Premium Concessions
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, opacity: 0.8 }}>
                    Elevating the classic movie snack experience
                  </Typography>
                  
                  <Typography paragraph sx={{ mb: 4 }}>
                    We've taken traditional movie concessions to the next level with premium ingredients, 
                    gourmet options, and customizable treats. From artisanal popcorn to locally-sourced 
                    candies, our concession stand offers the perfect complement to any film.
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Concession Highlights:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Typography component="li" paragraph>
                        Fresh-popped popcorn with gourmet seasonings
                      </Typography>
                      <Typography component="li" paragraph>
                        Unlimited refills on large popcorn and drinks for members
                      </Typography>
                      <Typography component="li" paragraph>
                        Locally-sourced snacks and artisanal treats
                      </Typography>
                      <Typography component="li" paragraph>
                        Specialty coffee and tea selection
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 6 }}>
                <Divider sx={{ mb: 6 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
                  Featured Concessions
                </Typography>
                <ConcessionMenu />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      
      {/* Membership Promo */}
      <Box sx={{ 
        py: 8, 
        bgcolor: 'background.paper',
        backgroundImage: 'linear-gradient(rgba(10, 25, 41, 0.8), rgba(10, 25, 41, 0.8)), url("/images/popcorn-bg.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        <Container maxWidth="md">
          <Paper 
            elevation={3}
            sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
              Exclusive Dining Perks
            </Typography>
            <Typography variant="h6" paragraph sx={{ mb: 4, color: 'white', opacity: 0.9 }}>
              Our members enjoy special dining benefits with every visit
            </Typography>
            
            <Grid container spacing={4} sx={{ mb: 4 }}>
              {[
                { tier: 'Supernova', benefit: '10% off all food and drinks' },
                { tier: 'Galaxy', benefit: '15% off all food and drinks plus free refills' },
                { tier: 'All Members', benefit: 'Exclusive access to themed menu items' },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'rgba(0, 0, 0, 0.2)', 
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main', mb: 1 }}>
                      {item.tier}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {item.benefit}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              href="/membership"
              sx={{ 
                px: 4, 
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Become a Member
            </Button>
          </Paper>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default Dining; 